import { isEmpty, roundOfToTwoDigits } from '@jfs/utils'
import { flow, types } from 'mobx-state-tree'
import { payoutCmsProcessApproval } from '../../network/payoutCmsProcessApproval'
import { searchTxnsReq } from '../../network/searchTxnsReq'
import { updateCmsReq } from '../../network/updateCmsReq'
import {
  DateFormatTypes,
  MessageTypes,
  dateFormatter,
  excelSerialToDate,
  getErrorMessage,
  getHHMMSS,
  isNumeric,
  isValidDateFormat,
} from '../../utils'
import { RootStore } from '../store'

const formatSettlementDate = (val: any) => {
  const date = new Date(val)
  return `${roundOfToTwoDigits(date.getDate())}/${roundOfToTwoDigits(date.getMonth() + 1)}/${date.getFullYear()}`
}
const Settlement = types.model('Settlement', {
  id: types.optional(types.string, ''),
  cmsNumber: types.optional(types.string, ''),
  settlementDate: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  mid: types.optional(types.string, ''),
  remarks: types.optional(types.string, ''),
  transactionId: types.maybeNull(types.string),
  requestType: types.maybeNull(types.string),
})

const PayoutCms = types
  .model('PayoutCms', {
    settlement: types.optional(Settlement, {}),
  })
  .actions((self) => ({
    searchSettlement: flow(function* searchSettlement(settlementId: string) {
      try {
        const startDate = `${dateFormatter(RootStore?.selectedDateInfo?.fromDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          RootStore?.selectedDateInfo?.fromDate,
        )}`
        const endDate = `${dateFormatter(RootStore?.selectedDateInfo?.toDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          RootStore?.selectedDateInfo?.toDate,
        )}`
        const req = {
          inputFilter: {
            startDateTime: startDate,
            endDateTime: endDate,
            payoutSequenceNumber: [settlementId],
            methodType: ['423'],
          },
          configParams: {
            mediaType: 'JSON',
            page: 1,
            size: 1,
          },
          outputDomains: [
            'methodType',
            'originalTransactionId',
            'jiopayOrigTransactionId',
            'merchantId',
            'settlementDate',
            'terminalId',
            'bankReferenceNumber',
            'invoice',
            'transDateTime',
            'paymentInstrument',
            'transactionType',
            'cardNumber',
            'settlementNetAmount',
            'settlementGrossAmount',
            'referenceNumber',
            'mdr',
            'igst',
            'ugst',
            'cgst',
            'sgst',
            'status',
            'externalReferenceNumber',
            'payoutSequenceNumber',
            'internalId',
          ],
        }
        const response = yield searchTxnsReq(req, RootStore.session.getTokens)
        const cmsSettlement = response?.content?.[0] //find((item) => item?.methodType === '423')
        if (cmsSettlement) {
          const settlementInfo = {
            id: cmsSettlement?.payoutSequenceNumber,
            cmsNumber: cmsSettlement?.externalReferenceNumber,
            settlementDate: `${new Date(cmsSettlement?.settlementDate)}`,
            status: cmsSettlement?.status ?? 'PAYEMNT_PENDING',
            mid: cmsSettlement?.merchantId,
            remarks: '',
          }
          self.settlement = settlementInfo
        } else {
          self.settlement = {} as any
          throw { message: 'No record found' }
        }
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    updateSettlement: flow(function* updateSettlement(values: any, payoutSequenceNumber: string) {
      try {
        const req = {
          entity: {
            type: 12,
            entityId: self?.settlement?.mid,
          },
          updateParameters: {
            payoutSequenceNumber: [payoutSequenceNumber],
          },
          requestType: 'PAYOUT_RESPONSE_UPDATE',
          payout: {
            status: values?.status,
            externalReferenceNumber: values?.cmsNumber,
            payoutDate: formatSettlementDate(values?.settlementDate),
            remarks: values?.remarks,
          },
        }
        const response = yield updateCmsReq(req, RootStore.session.getTokens)
        RootStore.message.setMessage('SUCCESS', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    bulkUpdate: flow(function* (txns: any) {
      const errorData: any = []
      for (let i = 0; i < txns.length; i++) {
        const data = txns[i]
        try {
          if (
            isEmpty(data?.MID) ||
            isEmpty(data?.['Payout Sequence Number']) ||
            isEmpty(data?.['Settlement Date']) ||
            isEmpty(data?.Status) ||
            isEmpty(data?.['CMS Number'])
          ) {
            errorData.push({ status: 'Failure', message: 'Data is missing', code: '' })
          } else {
            let settlementDate = data?.['Settlement Date']
            if (isNumeric(settlementDate)) {
              settlementDate = excelSerialToDate(Number(settlementDate))
            }
            if (!isValidDateFormat(settlementDate)) {
              throw { status: 'FAILURE', message: 'Settlement date should be of DD/MM/YYYY format' }
            }
            const req = {
              entity: {
                type: 12,
                entityId: data?.MID,
              },
              updateParameters: {
                payoutSequenceNumber: [data?.['Payout Sequence Number']],
              },
              requestType: 'PAYOUT_RESPONSE_UPDATE',
              payout: {
                status: data?.Status,
                externalReferenceNumber: data?.['CMS Number'],
                payoutDate: settlementDate,
                remarks: data?.remarks,
              },
            }
            const response = yield updateCmsReq(req, RootStore.session.getTokens)

            errorData.push({ status: 'SUCCESS', message: response?.message, code: '' })
          }
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, 200))
      }
      return errorData
    }),
    processApprovals: flow(function* processApprovals(data: any) {
      try {
        const response = yield payoutCmsProcessApproval(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? 'Approved', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
  }))
  .views((self) => ({
    getSetlementDetails() {
      const data = self?.settlement
      return { ...self?.settlement, status: '', currentStatus: data?.status }
    },
  }))

const payoutCmsData = PayoutCms.create({})

export { PayoutCms, payoutCmsData }
